import React, { useState, useEffect } from "react";

const LIFF_ID = "2009437803-25uwgX2N";

export default function ChatPage() {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [liffReady, setLiffReady] = useState(false);
  const [liffError, setLiffError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [language, setLanguage] = useState("jp");

  /* ── LIFF 初期化 ─────────────────────────────── */
  useEffect(() => {
    const initLiff = async () => {
      try {
        const liff = (await import("@line/liff")).default;
        await liff.init({ liffId: LIFF_ID });
        if (!liff.isLoggedIn()) {
          liff.login();
          return;
        }
        setLiffReady(true);
      } catch (e) {
        console.error("LIFF init failed:", e);
        setLiffError(e.message || "LIFF初期化に失敗しました");
      }
    };
    initLiff();
  }, []);

  /* ── フォーム変更 ────────────────────────────── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ── サマリー生成（送信内容は常に日本語キーで固定） ── */
  const buildSummary = () => {
    const parts = [];
    if (formData.rentMin || formData.rentMax) {
      const rentMin = formData.rentMin ? `${formData.rentMin}万円` : "";
      const rentMax = formData.rentMax ? `${formData.rentMax}万円` : "";
      parts.push(`賃料${rentMin}〜${rentMax}`);
    }
    if (formData.includeMaintenance) parts.push("共益費込み");
    if (formData.strictRent) parts.push("賃料融通不可");

    if (formData.sizeMin || formData.sizeMax) {
      const sizeMin = formData.sizeMin ? `${formData.sizeMin}㎡` : "";
      const sizeMax = formData.sizeMax ? `${formData.sizeMax}㎡` : "";
      parts.push(`専有面積${sizeMin}〜${sizeMax}`);
    }
    if (formData.flexibleSize) parts.push("面積融通可能");

    if (formData.station1) parts.push(`駅１ ${formData.station1}徒歩${formData.walk1 || "?"}分以内`);
    if (formData.commute) parts.push(`予定入居${formData.commute}前`);
    if (formData.station2) parts.push(`駅2 ${formData.station2}徒歩${formData.walk2 || "?"}分以内`);
    if (formData.station3) parts.push(`駅3 ${formData.station3}徒歩${formData.walk3 || "?"}分以内`);
    if (formData.strictTime) parts.push("時間融通不可");

    if (formData.age) parts.push(`*国籍（外国籍の方のみ）${formData.age}*`);
    if (formData.strictAge) parts.push("海外審査必要");
    if (formData.floor) parts.push(`*在留資格（外国籍の方のみ）${formData.floor}*`);
    if (formData.strictFloor) parts.push("日本語話せない");
    if (formData.facing) parts.push(`*フル学校名（留学生の方のみ）${formData.facing}*`);
    if (formData.strictFacing) parts.push("日本語学校学生");

    [
      "1R","1K","1DK","1LDK",
      "2K","2DK","2LDK",
      "3K","3DK/3LDK","3LDK以上","戸建",
    ].forEach((key) => { if (formData[key]) parts.push(`間取り${key}`); });

    [
      "築浅必要","二階以上必要","即入居可能",
      "二人入居","バス・トイレ別必要","インターネット無料必要",
      "洗濯機置き場必要","収納スペース必要","バルコニー必要",
      "RC必要","木造NG","ロフト必要",
      "ロフトNG","駐車場必要","庭必要",
      "南向必要","IHクッキングヒーターNG",
      "礼金なし必要","敷金なし必要",
    ].forEach((key) => { if (formData[key]) parts.push(key); });

    return parts.join(", ");
  };

  const summary = buildSummary();

  /* ── 送信 ───────────────────────────────────── */
  const handleSubmit = async () => {
    const message = summary;
    if (!message.trim()) {
      alert(L.alertEmpty);
      return;
    }
    try {
      setLoading(true);
      const liff = (await import("@line/liff")).default;
      await liff.sendMessages([
        { type: "text", text: `【希望条件】\n${message}` },
      ]);
      setSubmitted(true);
    } catch (error) {
      console.error("送信失败:", error);
      alert(L.alertFail);
    } finally {
      setLoading(false);
    }
  };

  /* ── i18n ───────────────────────────────────── */
  const t = {
    jp: {
      title: "希望条件入力",
      sectionRent: "💴 賃料",
      sectionArea: "📐 面積",
      sectionStation: "🚉 沿線・入居",
      sectionProfile: "👤 属性",
      sectionLayout: "🏠 間取り",
      sectionOther: "✅ その他条件",
      rentRange: "希望賃料範囲",
      includeMaintenance: "共益費込み",
      strictRent: "賃料融通不可",
      area: "希望専有面積",
      flexibleSize: "面積融通可能",
      station1: "第1希望駅（勤務先・学校最寄）",
      station2: "第2希望駅",
      station3: "第3希望駅",
      walk: "徒歩",
      walkUnit: "分以内",
      commute: "入居希望日（YY/MM/DD）",
      strictTime: "上記駅・距離以外不可",
      age: "国籍",
      strictAge: "海外審査必要",
      floor: "在留資格",
      strictFloor: "日本語での電話対応不可",
      facing: "学校・勤務先フルネーム",
      strictFacing: "日本語学校在籍",
      layouts: ["1R","1K","1DK","1LDK","2K","2DK","2LDK","3K","3DK/3LDK","3LDK以上","戸建"],
      layoutKeys: ["1R","1K","1DK","1LDK","2K","2DK","2LDK","3K","3DK/3LDK","3LDK以上","戸建"],
      optionKeys: [
        "築浅必要","二階以上必要","即入居可能",
        "二人入居","バス・トイレ別必要","インターネット無料必要",
        "洗濯機置き場必要","収納スペース必要","バルコニー必要",
        "RC必要","木造NG","ロフト必要",
        "ロフトNG","駐車場必要","庭必要",
        "南向必要","IHクッキングヒーターNG","礼金なし必要","敷金なし必要",
      ],
      optionLabels: [
        "築浅必要","2階以上必要","即入居可能",
        "二人入居可","バス・トイレ別必要","インターネット無料必要",
        "洗濯機置き場必要","収納スペース必要","バルコニー必要",
        "RC必要","木造NG","ロフト必要",
        "ロフトNG","駐車場必要","庭必要",
        "南向き必要","IHクッキングヒーターNG","礼金なし","敷金なし",
      ],
      submit: "送信",
      sending: "送信中...",
      successTitle: "送信完了！",
      successMsg: "希望条件をLINEに送信しました。",
      loading: "LIFFを初期化中...",
      preview: "📋 プレビュー",
      alertEmpty: "条件が未入力です。入力してください。",
      alertFail: "送信に失敗しました",
      minPlaceholder: "最小",
      maxPlaceholder: "最大",
    },
    en: {
      title: "Enter Desired Conditions",
      sectionRent: "💴 Rent",
      sectionArea: "📐 Area",
      sectionStation: "🚉 Station & Move-in",
      sectionProfile: "👤 Profile",
      sectionLayout: "🏠 Layout",
      sectionOther: "✅ Other Conditions",
      rentRange: "Desired Rent Range",
      includeMaintenance: "Including Maintenance Fee",
      strictRent: "Strict Rent Limit",
      area: "Desired Floor Area",
      flexibleSize: "Flexible on Area",
      station1: "1st Preferred Station (Work/School)",
      station2: "2nd Preferred Station",
      station3: "3rd Preferred Station",
      walk: "Walk",
      walkUnit: "min",
      commute: "Latest Move-in Date (YY/MM/DD)",
      strictTime: "Strict on above stations & distance",
      age: "Nationality",
      strictAge: "Requires Overseas Screening",
      floor: "Visa Status",
      strictFloor: "Cannot speak Japanese on phone",
      facing: "Full School / Company Name",
      strictFacing: "Japanese Language School Student",
      layouts: ["1R","1K","1DK","1LDK","2K","2DK","2LDK","3K","3DK/3LDK","3LDK+","House"],
      layoutKeys: ["1R","1K","1DK","1LDK","2K","2DK","2LDK","3K","3DK/3LDK","3LDK以上","戸建"],
      optionKeys: [
        "築浅必要","二階以上必要","即入居可能",
        "二人入居","バス・トイレ別必要","インターネット無料必要",
        "洗濯機置き場必要","収納スペース必要","バルコニー必要",
        "RC必要","木造NG","ロフト必要",
        "ロフトNG","駐車場必要","庭必要",
        "南向必要","IHクッキングヒーターNG","礼金なし必要","敷金なし必要",
      ],
      optionLabels: [
        "Built within 10 yrs","Not 1st floor","Immediate Move-in",
        "Two Persons OK","Separate Bath/Toilet","Free Wi-Fi",
        "Washing Machine Space","Storage Space","Balcony",
        "RC Structure","No Wood Structure","Has Loft",
        "No Loft","Parking","Garden",
        "South Facing","No IH Stove","No Key Money","No Deposit",
      ],
      submit: "Submit",
      sending: "Sending...",
      successTitle: "Sent!",
      successMsg: "Your conditions have been sent via LINE.",
      loading: "Initializing LIFF...",
      preview: "📋 Preview",
      alertEmpty: "Please enter at least one condition.",
      alertFail: "Failed to send. Please try again.",
      minPlaceholder: "Min",
      maxPlaceholder: "Max",
    },
    zh: {
      title: "填写希望条件",
      sectionRent: "💴 租金",
      sectionArea: "📐 面积",
      sectionStation: "🚉 车站・入住",
      sectionProfile: "👤 个人信息",
      sectionLayout: "🏠 户型",
      sectionOther: "✅ 其他条件",
      rentRange: "希望租金范围",
      includeMaintenance: "含管理费",
      strictRent: "租金不可商量",
      area: "希望专有面积",
      flexibleSize: "面积可商量",
      station1: "第1希望车站（工作/学校最近）",
      station2: "第2希望车站",
      station3: "第3希望车站",
      walk: "步行",
      walkUnit: "分钟以内",
      commute: "最迟入住日期（YY/MM/DD）",
      strictTime: "仅接受上述车站和距离",
      age: "国籍",
      strictAge: "需要海外审查",
      floor: "在留资格",
      strictFloor: "无法用日语接听电话",
      facing: "学校/工作单位全名",
      strictFacing: "日语学校在校生",
      layouts: ["1R","1K","1DK","1LDK","2K","2DK","2LDK","3K","3DK/3LDK","3LDK以上","独栋"],
      layoutKeys: ["1R","1K","1DK","1LDK","2K","2DK","2LDK","3K","3DK/3LDK","3LDK以上","戸建"],
      optionKeys: [
        "築浅必要","二階以上必要","即入居可能",
        "二人入居","バス・トイレ別必要","インターネット無料必要",
        "洗濯機置き場必要","収納スペース必要","バルコニー必要",
        "RC必要","木造NG","ロフト必要",
        "ロフトNG","駐車場必要","庭必要",
        "南向必要","IHクッキングヒーターNG","礼金なし必要","敷金なし必要",
      ],
      optionLabels: [
        "房龄10年以内","2楼以上","可即时入住",
        "两人同住","浴室厕所分开","免费网络",
        "有洗衣机位置","有收纳空间","有阳台",
        "RC结构","不要木结构","需要LOFT",
        "不要LOFT","需要停车位","需要院子",
        "朝南","不要IH灶台","无礼金","无押金",
      ],
      submit: "发送",
      sending: "发送中...",
      successTitle: "发送成功！",
      successMsg: "希望条件已通过LINE发送。",
      loading: "正在初始化LIFF...",
      preview: "📋 预览",
      alertEmpty: "请至少填写一项条件。",
      alertFail: "发送失败，请重试。",
      minPlaceholder: "最小",
      maxPlaceholder: "最大",
    },
  };

  const L = t[language];

  /* ── ローディング / エラー画面 ─────────────── */
  if (liffError) {
    return (
      <div style={styles.center}>
        <p style={{ color: "#e55" }}>⚠ {liffError}</p>
      </div>
    );
  }

  if (!liffReady) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
        <p style={{ color: "#06C755", marginTop: 12, fontWeight: 600 }}>{t[language].loading}</p>
      </div>
    );
  }

  /* ── 送信成功画面 ────────────────────────────── */
  if (submitted) {
    return (
      <div style={styles.center}>
        <div style={styles.successIcon}>✓</div>
        <h2 style={styles.successTitle}>{L.successTitle}</h2>
        <p style={styles.successMsg}>{L.successMsg}</p>
      </div>
    );
  }

  /* ── メインフォーム ──────────────────────────── */
  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <div style={styles.lineDot} />
        <h1 style={styles.title}>{L.title}</h1>
        <div style={styles.langGroup}>
          {[
            { key: "jp", label: "JP" },
            { key: "en", label: "EN" },
            { key: "zh", label: "中文" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setLanguage(key)}
              style={{
                ...styles.langBtn,
                background: language === key ? "#fff" : "rgba(255,255,255,0.15)",
                color: language === key ? "#06C755" : "#fff",
                fontWeight: language === key ? 700 : 400,
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={styles.body}>
        {/* ── 賃料 ── */}
        <Section label={L.sectionRent}>
          <div style={styles.row}>
            <span style={styles.rowLabel}>{L.rentRange}</span>
            <div style={styles.rangeGroup}>
              <input style={styles.input} name="rentMin" onChange={handleChange} placeholder={L.minPlaceholder} />
              <span style={styles.unit}>万円〜</span>
              <input style={styles.input} name="rentMax" onChange={handleChange} placeholder={L.maxPlaceholder} />
              <span style={styles.unit}>万円</span>
            </div>
          </div>
          <div style={styles.checkRow}>
            <CheckBox name="includeMaintenance" label={L.includeMaintenance} onChange={handleChange} />
            <CheckBox name="strictRent" label={L.strictRent} onChange={handleChange} />
          </div>
        </Section>

        {/* ── 面積 ── */}
        <Section label={L.sectionArea}>
          <div style={styles.row}>
            <span style={styles.rowLabel}>{L.area}</span>
            <div style={styles.rangeGroup}>
              <input style={styles.input} name="sizeMin" onChange={handleChange} placeholder={L.minPlaceholder} />
              <span style={styles.unit}>㎡〜</span>
              <input style={styles.input} name="sizeMax" onChange={handleChange} placeholder={L.maxPlaceholder} />
              <span style={styles.unit}>㎡</span>
            </div>
          </div>
          <div style={styles.checkRow}>
            <CheckBox name="flexibleSize" label={L.flexibleSize} onChange={handleChange} />
          </div>
        </Section>

        {/* ── 沿線 ── */}
        <Section label={L.sectionStation}>
          {[
            { stKey: "station1", wKey: "walk1", label: L.station1 },
            { stKey: "station2", wKey: "walk2", label: L.station2 },
            { stKey: "station3", wKey: "walk3", label: L.station3 },
          ].map(({ stKey, wKey, label }) => (
            <div key={stKey} style={styles.stationRow}>
              <span style={styles.stationLabel}>{label}</span>
              <input style={{ ...styles.input, flex: 1, width: "auto" }} name={stKey} onChange={handleChange} />
              <span style={styles.unit}>{L.walk}</span>
              <input style={{ ...styles.input, width: 40 }} name={wKey} onChange={handleChange} placeholder="?" />
              <span style={styles.unit}>{L.walkUnit}</span>
            </div>
          ))}
          <div style={styles.row}>
            <span style={styles.rowLabel}>{L.commute}</span>
            <input style={{ ...styles.input, flex: 1, width: "auto" }} name="commute" onChange={handleChange} placeholder="YY/MM/DD" />
          </div>
          <div style={styles.checkRow}>
            <CheckBox name="strictTime" label={L.strictTime} onChange={handleChange} />
          </div>
        </Section>

        {/* ── 属性 ── */}
        <Section label={L.sectionProfile}>
          {[
            { key: "age",    label: L.age,    cbKey: "strictAge",    cbLabel: L.strictAge },
            { key: "floor",  label: L.floor,  cbKey: "strictFloor",  cbLabel: L.strictFloor },
            { key: "facing", label: L.facing, cbKey: "strictFacing", cbLabel: L.strictFacing },
          ].map(({ key, label, cbKey, cbLabel }) => (
            <div key={key} style={{ marginBottom: 10 }}>
              <div style={styles.row}>
                <span style={styles.rowLabel}>{label}</span>
                <input style={{ ...styles.input, flex: 1, width: "auto" }} name={key} onChange={handleChange} />
              </div>
              <div style={styles.checkRow}>
                <CheckBox name={cbKey} label={cbLabel} onChange={handleChange} />
              </div>
            </div>
          ))}
        </Section>

        {/* ── 間取り ── */}
        <Section label={L.sectionLayout}>
          <div style={styles.chipGrid}>
            {L.layouts.map((label, i) => (
              <ChipCheckBox key={L.layoutKeys[i]} name={L.layoutKeys[i]} label={label} onChange={handleChange} />
            ))}
          </div>
        </Section>

        {/* ── その他条件 ── */}
        <Section label={L.sectionOther}>
          <div style={styles.chipGrid}>
            {L.optionLabels.map((label, i) => (
              <ChipCheckBox key={L.optionKeys[i]} name={L.optionKeys[i]} label={label} onChange={handleChange} />
            ))}
          </div>
        </Section>

        {/* ── プレビュー ── */}
        {summary && (
          <div style={styles.preview}>
            <div style={styles.previewLabel}>{L.preview}</div>
            <p style={styles.previewText}>{summary}</p>
          </div>
        )}

        {/* ── 送信ボタン ── */}
        <button
          style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? L.sending : `${L.submit} →`}
        </button>
      </div>
    </div>
  );
}

/* ── サブコンポーネント ─────────────────────────── */
function Section({ label, children }) {
  return (
    <div style={styles.section}>
      <div style={styles.sectionLabel}>{label}</div>
      {children}
    </div>
  );
}

function CheckBox({ name, label, onChange }) {
  return (
    <label style={styles.checkLabel}>
      <input type="checkbox" name={name} onChange={onChange} style={styles.checkInput} />
      <span style={styles.checkText}>{label}</span>
    </label>
  );
}

function ChipCheckBox({ name, label, onChange }) {
  const [checked, setChecked] = useState(false);
  const toggle = () => {
    setChecked((v) => !v);
    onChange({ target: { name, type: "checkbox", checked: !checked } });
  };
  return (
    <div
      onClick={toggle}
      style={{
        ...styles.chip,
        background: checked ? "#06C755" : "#f0f0f0",
        color: checked ? "#fff" : "#333",
        borderColor: checked ? "#06C755" : "#ddd",
      }}
    >
      {label}
    </div>
  );
}

/* ── スタイル ────────────────────────────────────── */
const styles = {
  container: {
    maxWidth: 480,
    margin: "0 auto",
    minHeight: "100vh",
    background: "#f7f8fa",
    fontFamily: "'Noto Sans JP', 'Helvetica Neue', sans-serif",
    fontSize: 13,
    color: "#222",
    paddingBottom: 40,
  },
  header: {
    background: "#06C755",
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    position: "sticky",
    top: 0,
    zIndex: 10,
    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
  },
  lineDot: {
    width: 10, height: 10,
    borderRadius: "50%",
    background: "#fff",
    flexShrink: 0,
  },
  title: {
    flex: 1,
    fontSize: 16,
    fontWeight: 700,
    color: "#fff",
    margin: 0,
    letterSpacing: 0.5,
  },
  langGroup: {
    display: "flex",
    gap: 4,
  },
  langBtn: {
    border: "1px solid rgba(255,255,255,0.5)",
    borderRadius: 20,
    padding: "3px 9px",
    fontSize: 11,
    cursor: "pointer",
    userSelect: "none",
    transition: "all 0.15s",
  },
  body: { padding: "12px 14px" },
  section: {
    background: "#fff",
    borderRadius: 12,
    padding: "14px 16px",
    marginBottom: 12,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#06C755",
    marginBottom: 10,
    letterSpacing: 0.8,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 6,
    flexWrap: "wrap",
  },
  rowLabel: { fontSize: 12, color: "#555", minWidth: 80, flexShrink: 0 },
  rangeGroup: { display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" },
  input: {
    border: "1px solid #ddd",
    borderRadius: 6,
    padding: "5px 8px",
    fontSize: 13,
    outline: "none",
    width: 70,
    transition: "border 0.2s",
    background: "#fafafa",
  },
  unit: { fontSize: 12, color: "#666" },
  checkRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 4,
  },
  checkLabel: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    cursor: "pointer",
    userSelect: "none",
  },
  checkInput: { accentColor: "#06C755", width: 14, height: 14 },
  checkText: { fontSize: 12, color: "#444" },
  stationRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginBottom: 8,
    flexWrap: "wrap",
  },
  stationLabel: { fontSize: 11, color: "#555", minWidth: 80 },
  chipGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  chip: {
    border: "1px solid #ddd",
    borderRadius: 20,
    padding: "5px 12px",
    fontSize: 12,
    cursor: "pointer",
    userSelect: "none",
    transition: "all 0.15s",
    fontWeight: 500,
  },
  preview: {
    background: "#fff",
    border: "1px dashed #06C755",
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: 16,
  },
  previewLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#06C755",
    marginBottom: 6,
  },
  previewText: {
    fontSize: 12,
    color: "#333",
    lineHeight: 1.7,
    margin: 0,
    wordBreak: "break-all",
  },
  submitBtn: {
    width: "100%",
    background: "#06C755",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "14px 0",
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 1,
    cursor: "pointer",
    transition: "opacity 0.2s, transform 0.1s",
    boxShadow: "0 4px 16px rgba(6,199,85,0.35)",
  },
  center: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "#f7f8fa",
    fontFamily: "'Noto Sans JP', sans-serif",
  },
  spinner: {
    width: 36,
    height: 36,
    border: "3px solid #eee",
    borderTop: "3px solid #06C755",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  successIcon: {
    width: 64, height: 64,
    background: "#06C755",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 32,
    color: "#fff",
    marginBottom: 16,
    boxShadow: "0 4px 20px rgba(6,199,85,0.4)",
  },
  successTitle: {
    fontSize: 22, fontWeight: 700, color: "#222", margin: "0 0 8px",
  },
  successMsg: {
    fontSize: 14, color: "#666",
  },
};